/**
 * TeamStrength Hub - Dashboard Layout with Sidebar
 */
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Users, LayoutDashboard, UsersRound, Layers, UserCog, Shield,
  ChevronDown, LogOut, Building2, Menu, X, Moon, Sun, User, Sparkles
} from 'lucide-react';

const DashboardLayout = () => {
  const { user, currentOrg, organizations, switchOrg, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const navItems = [
    { to: '/dashboard/studio', icon: LayoutDashboard, label: 'Studio', roles: ['org_admin'] },
    { to: '/dashboard/teams', icon: UsersRound, label: 'Teams', roles: ['org_admin', 'team_admin', 'member'] },
    { to: '/dashboard/model', icon: Layers, label: 'Model', roles: ['org_admin'] },
    { to: '/dashboard/users', icon: UserCog, label: 'Users', roles: ['org_admin'] },
  ];

  const currentRole = currentOrg?.role;

  const filteredNavItems = navItems.filter(item => 
    isSuperAdmin || (currentRole && item.roles.includes(currentRole))
  );

  return (
    <div className={`app-layout ${darkMode ? 'dark' : ''}`}>
      {/* Mobile menu button */}
      <button
        className="fixed top-4 left-4 z-50 md:hidden p-2 rounded-lg bg-white dark:bg-slate-900 shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
                  TeamStrength
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">Hub</p>
              </div>
            </div>
          </div>

          {/* Org Switcher */}
          {currentOrg && (
            <div className="p-4 border-b border-slate-200 dark:border-slate-800">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between"
                    data-testid="org-switcher-btn"
                  >
                    <div className="flex items-center gap-2 truncate">
                      <Building2 className="w-4 h-4 text-violet-600" />
                      <span className="truncate">{currentOrg.name}</span>
                    </div>
                    <ChevronDown className="w-4 h-4 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {organizations.map((org) => (
                    <DropdownMenuItem 
                      key={org.org_id}
                      onClick={() => switchOrg(org)}
                      className={org.org_id === currentOrg.org_id ? 'bg-violet-50 dark:bg-violet-900/20' : ''}
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      {org.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 text-center capitalize">
                {currentRole?.replace('_', ' ')}
              </p>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item relative ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}

            {isSuperAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Admin
                  </p>
                </div>
                <NavLink
                  to="/dashboard/admin"
                  className={({ isActive }) => `nav-item relative ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid="nav-admin"
                >
                  <Shield className="w-5 h-5" />
                  <span>SuperAdmin</span>
                </NavLink>
              </>
            )}
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                data-testid="dark-mode-toggle"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 px-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-violet-100 text-violet-700 text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left truncate">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')} data-testid="profile-link">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Mein Stärkenprofil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
